import { Grid, } from '@mui/material';
import { deleteStaff, resetPassword } from 'api/staff';
import CustomReactSelect from 'components/CustomReactSelect';
import Loading from 'components/Loading';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getRoleFilters, getStaffStatusFilters } from 'utils/helper';
import { parentRoute } from ".";

const UserForm = ({ formData = {}, handleChange, handleSubmit, buttonText = "Submit", isEdit = false, isLoading, formErrors = {} }) => {
    const roleSelection = getRoleFilters(false)
    const statusSelection = getStaffStatusFilters(false)
    const [newResetPassword, setNewResetPassword] = useState('');
    const navigate = useNavigate();

    if (isLoading && isEdit && !formData.id) {
        return (
            <Loading />
        );
    }

    const handleResetPassword = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert previous password and new password will only show 1 time",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            const { data } = await resetPassword(formData.id);
            setNewResetPassword(data?.data?.otp)
            toast.success("Staff password reset successfully");
        }
    }

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            await deleteStaff(formData.id)
            toast.success("Staff deleted successfully");
            navigate(parentRoute);
        }
    }

    return (
        <MDBox pt={3} px={2} pb={2}>
            <form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <MDInput
                            fullWidth
                            label="Full Name"
                            name="sf_full_name"
                            value={formData.sf_full_name || ''}
                            onChange={handleChange}
                            required
                            placeholder="at least 3 characters and maximum 100 characters"
                            inputProps={{ maxLength: 100 }}
                            error={formErrors?.sf_full_name}
                            helperText={formErrors?.sf_full_name}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDInput
                            fullWidth
                            label="Start Working Date"
                            name="sf_start_working_date"
                            type="date"
                            value={formData.sf_start_working_date || ''}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={formErrors?.sf_start_working_date}
                            helperText={formErrors?.sf_start_working_date}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomReactSelect
                            label="Role"
                            name="sf_role"
                            placeholder="All Roles"
                            variant="outlined"
                            value={roleSelection.find(role => role.value === (formData.sf_role))}
                            onChange={(selectedOption) => handleChange({ target: { name: 'sf_role', value: selectedOption?.value } })}
                            options={roleSelection}
                            required
                            disabled={isEdit}
                            error={formErrors?.sf_role}
                            helperText={formErrors?.sf_role}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDInput
                            fullWidth
                            label="Username"
                            placeholder="This will be used for login"
                            name="sf_username"
                            value={formData.sf_username || ''}
                            onChange={handleChange}
                            required
                            inputProps={{ maxLength: 50 }}
                            disabled={isEdit}
                            error={formErrors?.sf_username}
                            helperText={formErrors?.sf_username}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <MDInput
                            fullWidth
                            label="Description"
                            name="sf_description"
                            value={formData.sf_description || ''}
                            onChange={handleChange}
                            multiline
                            minRows={3}
                            inputProps={{ maxLength: 200 }}
                            error={formErrors?.sf_description}
                            helperText={formErrors?.sf_description}
                        />
                    </Grid>

                    {isEdit && <Grid item xs={12} md={6}>
                        <CustomReactSelect
                            label="Status"
                            name="sf_status"
                            variant="outlined"
                            value={statusSelection.find(status => status.value === formData.sf_status ?? formData.sf_status?.id)}
                            onChange={(selectedOption) => handleChange({ target: { name: 'sf_status', value: selectedOption?.value } })}
                            options={statusSelection}
                            required
                            error={formErrors?.sf_status}
                            helperText={formErrors?.sf_status}
                        />
                    </Grid>}

                    <Grid item xs={12}>
                        <Grid container>
                            {isEdit && (
                                <Grid item>
                                    <MDButton
                                        variant="contained"
                                        color="error"
                                        onClick={handleDelete}
                                        loading={isLoading}
                                        sx={{ mr: 2 }}
                                    >
                                        Delete
                                    </MDButton>
                                </Grid>
                            )}
                            <Grid item xs />
                            <Grid item>
                                <MDBox display="flex" justifyContent="flex-end">
                                    {isEdit && (
                                        <MDButton
                                            variant="contained"
                                            color="warning"
                                            onClick={handleResetPassword}
                                            loading={isLoading}
                                            sx={{ mr: 2 }}
                                        >
                                            Reset Password
                                        </MDButton>
                                    )}
                                    <MDButton
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        loading={isLoading}
                                    >
                                        {buttonText}
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </MDBox>
    );
};

export default UserForm;

UserForm.propTypes = {
    formData: PropTypes.shape({
        username: PropTypes.string,
        email: PropTypes.string,
        password: PropTypes.string,
        department: PropTypes.string,
        role: PropTypes.string,
        client: PropTypes.string,
        custom_permissions: PropTypes.arrayOf(PropTypes.string)
    }),
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    buttonText: PropTypes.string,
    isEdit: PropTypes.bool,
    isLoading: PropTypes.bool
};
